/**
 * Phase 27.37.A — Settings → Integrations → Twilio (KR admin only).
 *
 * Read-only dashboard that shows the health of every Twilio pillar:
 * credentials, Messaging Service SID, A2P/Business-Compliance status,
 * webhook URLs to paste into Twilio, and the Advanced Opt-Out keyword
 * catalogue.
 *
 * Never displays raw secrets — only masked previews returned by the
 * backend (`AC…7f2c`). Credentials live exclusively in `backend/.env`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Copy, Loader2, ShieldAlert, MessageSquare,
  Webhook, Settings2, Info, KeyRound, ArrowLeft, RefreshCw, Radio, Hash, Search,
  AlertCircle, Circle, Send,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { smsIntegrationAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { SmsUsagePanel } from '@/components/sms/SmsUsagePanel';

const STATUS_PILL = (ok, label) => (
  <Badge
    variant="outline"
    className={`border font-normal ${ok
      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
      : 'bg-slate-100 text-slate-600 border-slate-300'}`}
  >
    {ok ? <CheckCircle2 size={11} className="mr-1" /> : <XCircle size={11} className="mr-1" />}
    {label}
  </Badge>
);

export default function TwilioIntegrationPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await smsIntegrationAPI.status();
      setStatus(r.data);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load Twilio status'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTrusthubRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await smsIntegrationAPI.trusthubRefresh();
      if (r.data?.live) {
        toast.success('Refreshed from Twilio TrustHub');
      } else {
        toast.info('TrustHub sync is off or credentials are missing — showing manual env values');
      }
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to refresh from Twilio'));
    } finally { setRefreshing(false); }
  };

  const copy = (v) => { if (!v) return; navigator.clipboard.writeText(v); toast.success('Copied'); };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center gap-2 text-slate-500">
        <Loader2 size={16} className="animate-spin" /> Loading Twilio status…
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="p-4 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">
          {error}
        </div>
      </div>
    );
  }
  if (!status) return null;

  const connected = status.connection_status === 'connected';
  const hasWebhookBase = !!status.webhook_urls?.base_url_configured;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="twilio-integration-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="text-sky-600" />
            <h1 className="text-2xl font-bold text-slate-900">Twilio · SMS Integration</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            KR Agency Hub Messaging service. Credentials are stored in the backend
            environment — this page never exposes raw secrets.
          </p>
        </div>
        {connected
          ? <Badge className="bg-emerald-500 text-white border-emerald-500">Connected</Badge>
          : <Badge className="bg-amber-500 text-white border-amber-500">Not connected</Badge>}
      </div>

      {/* Phase 27.37.I — Inbound Webhook Diagnostics.
          Persists a breadcrumb every time Twilio hits the inbound
          webhook so operators can see (from the UI) whether Twilio
          is delivering AND whether the signature validated. */}
      <InboundDiagnosticsCard />
      <PhoneDiagnosticsCard />

      {/* Phase 27.38.2 — Uso de SMS por agente (últimos 7 / 14 / 30 días). */}
      <SmsUsagePanel />

      {/* Compliance banner — split Brand vs Campaign so we never
          falsely claim Approved when only the Brand is approved. */}
      <div
        className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-2 text-sm"
        data-testid="twilio-compliance-banner"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill label="Business / Brand" value={status.a2p_brand_status || status.compliance_profile_status} />
            <StatusPill label="A2P Campaign" value={status.a2p_campaign_status || status.a2p_registration_status} />
            {status.status_source === 'twilio_trusthub'
              ? (
                <Badge className="bg-sky-100 text-sky-800 border-sky-300 border font-normal" data-testid="status-source-live">
                  <Radio size={11} className="mr-1" />Live
                </Badge>
              )
              : (
                <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 font-normal" data-testid="status-source-manual">
                  Manual env
                </Badge>
              )
            }
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleTrusthubRefresh}
            disabled={refreshing}
            data-testid="trusthub-refresh-btn"
          >
            {refreshing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
            Refresh from Twilio
          </Button>
        </div>
        {status.last_synced_at && (
          <p className="text-[11px] text-slate-500" data-testid="trusthub-last-synced">
            Last synced from Twilio TrustHub: <span className="font-mono">{status.last_synced_at}</span>
          </p>
        )}
        {status.status_source !== 'twilio_trusthub' && (
          <p className="text-[11px] text-slate-500 leading-relaxed">
            <span className="font-semibold">Values below are manually configured</span>{' '}
            via `TWILIO_BRAND_STATUS` and `TWILIO_CAMPAIGN_STATUS` env vars —
            they do NOT reflect a live Twilio TrustHub check. Set{' '}
            <code className="bg-slate-100 px-1 py-0.5 rounded">TWILIO_A2P_TRUSTHUB_SYNC=true</code>
            {' '}and provide API credentials to enable live sync.
          </p>
        )}
      </div>

      {/* Messaging Service pool — only rendered when live TrustHub
          returned Twilio's actual number list. Kept invisible in
          manual-env mode so we never fabricate a number roster. */}
      {(status.messaging_service_numbers?.length > 0 || status.messaging_service_details) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Hash size={16} className="text-slate-600" />
              Messaging Service — Customer live pool
            </CardTitle>
            {status.agent_channel?.ready && (
              <p className="text-[11px] text-slate-500 pt-1">
                Only the <strong>Customer</strong> Messaging Service pool is listed here.
                Scroll down to <strong>Dual-Channel Setup</strong> to see the separate
                <strong> Agent</strong> number (<span className="font-mono">{status.agent_channel?.phone_number}</span>)
                and its own MSSID.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {status.messaging_service_details && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <KV label="Friendly name" value={status.messaging_service_details.friendly_name || '—'} />
                <KV label="Use case" value={status.messaging_service_details.usecase || '—'} />
                <KV label="US A2P registered" value={boolLabel(status.messaging_service_details.us_a2p_registered)} />
              </div>
            )}
            {status.messaging_service_numbers?.length > 0 && (
              <div className="rounded border border-slate-200 divide-y divide-slate-100" data-testid="ms-numbers-list">
                {status.messaging_service_numbers.map((n) => (
                  <div key={n.sid} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-mono text-slate-800">{n.phone_number || '—'}</span>
                    <span className="text-[11px] text-slate-500">{n.iso_country || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Credentials & Messaging Service */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound size={16} className="text-slate-600" />
            Credentials &amp; Messaging Service
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ConfigRow label="Account SID"
            configured={status.account_sid?.configured}
            preview={status.account_sid?.preview}
            envKey="TWILIO_ACCOUNT_SID"
            testId="cfg-account-sid"
          />
          <ConfigRow label="API Key"
            configured={status.api_key?.configured}
            preview={status.api_key?.preview}
            envKey="TWILIO_API_KEY"
            testId="cfg-api-key"
          />
          <ConfigRow label="API Secret"
            configured={status.api_secret_configured}
            envKey="TWILIO_API_SECRET"
            testId="cfg-api-secret"
          />
          <ConfigRow label="Auth Token (webhook signing)"
            configured={status.auth_token_configured}
            envKey="TWILIO_AUTH_TOKEN"
            testId="cfg-auth-token"
          />
          <ConfigRow label="Messaging Service SID"
            configured={status.messaging_service_sid?.configured}
            preview={status.messaging_service_sid?.preview}
            envKey="TWILIO_MESSAGING_SERVICE_SID"
            testId="cfg-msg-service"
          />
          <ConfigRow label="Phone Number"
            configured={!!status.phone_number}
            preview={status.phone_number || '—'}
            envKey="TWILIO_PHONE_NUMBER"
            testId="cfg-phone"
          />
        </CardContent>
      </Card>

      {/* Phase 27.37.M — Dual-channel view (Customer +863 vs Agent +813).
          Answers the recurring "why don't I see the other number?"
          question by showing BOTH channels side-by-side with their
          own MSSID + phone + readiness state. */}
      <Card data-testid="twilio-channels-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio size={16} className="text-slate-600" />
            Dual-Channel Setup
          </CardTitle>
          <p className="text-xs text-slate-500">
            KR Agency Hub uses TWO Twilio Messaging Services: one for
            customer-facing SMS (leads / prospects) and a separate one
            for internal Agent SMS (opt-in only). Each channel has its
            own number + Messaging Service SID.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChannelCard
            channel="customer"
            title="Customer channel"
            ready={!!status.customer_channel?.phone_number}
            number={status.customer_channel?.phone_number}
            mssidPreview={status.customer_channel?.messaging_service_sid?.preview}
            envNumber="TWILIO_PHONE_NUMBER_CUSTOMER"
            envMssid="TWILIO_MESSAGING_SERVICE_SID_CUSTOMER"
            notReadyHint={null}
          />
          <ChannelCard
            channel="agent"
            title="Agent channel"
            ready={!!status.agent_channel?.ready}
            number={status.agent_channel?.phone_number}
            mssidPreview={status.agent_channel?.messaging_service_sid?.preview}
            envNumber="TWILIO_PHONE_NUMBER_AGENT"
            envMssid="TWILIO_MESSAGING_SERVICE_SID_AGENT"
            notReadyHint={!status.agent_channel?.ready ? (
              <div className="text-amber-700 text-[11px] mt-2 pt-2 border-t border-amber-200">
                The Agent channel isn&apos;t provisioned in this env.
                Ask Emergent Support to add the agent-side env vars so
                broadcasts, event reminders, and follow-ups can send.
              </div>
            ) : null}
          />
        </CardContent>
      </Card>

      {/* Overall pillars */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings2 size={16} className="text-slate-600" />
            Twilio pillars
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <PillarRow label="Account SID" ok={status.account_sid?.configured} />
            <PillarRow label="Messaging Service SID" ok={status.messaging_service_sid?.configured} />
            <PillarRow label="Phone Number" ok={!!status.phone_number} soft="Managed by Messaging Service when SID is set" />
            <PillarRow label="Business / Brand approval"
              ok={(status.a2p_brand_status || '') === 'approved'}
              statusLabel={status.a2p_brand_status}
            />
            <PillarRow label="A2P Campaign approval"
              ok={(status.a2p_campaign_status || '') === 'approved'}
              statusLabel={status.a2p_campaign_status}
            />
            <PillarRow label="Incoming SMS"
              ok={status.incoming_sms_status === 'ready'}
              statusLabel={status.incoming_sms_status}
            />
            <PillarRow label="Delivery Callback"
              ok={status.delivery_callback_status === 'ready'}
              statusLabel={status.delivery_callback_status}
            />
            <PillarRow label="Advanced Opt-Out"
              ok={status.advanced_opt_out_status === 'enabled'}
              statusLabel={status.advanced_opt_out_status}
            />
          </div>
        </CardContent>
      </Card>

      {/* Messaging Service defaults */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Info size={16} className="text-slate-600" />
            Messaging Service defaults
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <KV label="Region" value={status.messaging_service_defaults?.region} />
          <KV label="Sticky Sender" value={boolLabel(status.messaging_service_defaults?.sticky_sender)} />
          <KV label="Area Code Geomatch" value={boolLabel(status.messaging_service_defaults?.area_code_geomatch)} />
          <KV label="Smart Encoding" value={boolLabel(status.messaging_service_defaults?.smart_encoding)} />
          <KV label="MMS Converter" value={boolLabel(status.messaging_service_defaults?.mms_converter)} />
          <KV label="Validity period" value={`${status.messaging_service_defaults?.validity_period_seconds}s`} />
          <KV label="Advanced Opt-Out" value={boolLabel(status.messaging_service_defaults?.advanced_opt_out)} />
        </CardContent>
      </Card>

      {/* Webhook URLs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Webhook size={16} className="text-slate-600" />
            Webhook URLs to paste in Twilio
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!hasWebhookBase && (
            <div className="p-2 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-800">
              <span className="font-semibold">WEBHOOK_BASE_URL is not set.</span> Set it in
              the backend `.env` — must be the public origin (preview or production
              domain) — otherwise inbound webhooks cannot be verified.
            </div>
          )}
          <WebhookRow
            label="Inbound Messages"
            url={status.webhook_urls?.inbound_message_webhook}
            hint="Messaging Service → Sender Pool → Inbound Messages → Send a webhook (POST)"
            testId="webhook-inbound"
            onCopy={copy}
          />
          <WebhookRow
            label="Delivery Status Callback"
            url={status.webhook_urls?.delivery_status_callback}
            hint="Messaging Service → Integration → Delivery Status Callback (POST)"
            testId="webhook-status"
            onCopy={copy}
          />
          <p className="text-[11px] text-slate-500 leading-relaxed">{status.webhook_urls?.note}</p>
        </CardContent>
      </Card>

      {/* Missing / next steps + Env Var Health Check */}
      <Card data-testid="twilio-env-health-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert
              size={16}
              className={
                status.env_health?.status === 'broken' ? 'text-rose-500'
                : status.env_health?.status === 'degraded' ? 'text-amber-500'
                : 'text-emerald-500'
              }
            />
            Env Var Health Check
            {status.env_health?.status && (
              <Badge
                className={
                  status.env_health.status === 'broken' ? 'bg-rose-100 text-rose-800 border-rose-300'
                  : status.env_health.status === 'degraded' ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-emerald-100 text-emerald-800 border-emerald-300'
                }
              >
                {status.env_health.status}
              </Badge>
            )}
          </CardTitle>
          <p className="text-[11px] text-slate-500 pt-1">
            Every Twilio-related env var that this runtime is (or should be)
            reading. Aliases are honoured — a key shows as set if any of its
            backward-compat aliases are set in .env.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {['core', 'customer', 'agent', 'webhook'].map((group) => {
            const rows = (status.env_health?.rows || []).filter((r) => r.group === group);
            if (!rows.length) return null;
            const gStat = (status.env_health?.groups || {})[group] || {};
            const label = {
              core: 'Core auth & default sender',
              customer: 'Customer channel (leads / prospects)',
              agent: 'Agent channel (internal opt-in SMS)',
              webhook: 'Webhook base URL',
            }[group];
            const gBadge = gStat.required_missing > 0
              ? { cls: 'bg-rose-100 text-rose-800 border-rose-300', text: `${gStat.required_missing} required missing` }
              : gStat.recommended_missing > 0
              ? { cls: 'bg-amber-100 text-amber-800 border-amber-300', text: `${gStat.recommended_missing} recommended missing` }
              : { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', text: 'All set' };
            return (
              <div key={group} className="rounded-md border border-slate-200 overflow-hidden" data-testid={`env-health-group-${group}`}>
                <div className="flex items-center justify-between bg-slate-50 px-3 py-1.5">
                  <div className="text-xs font-semibold text-slate-700">{label}</div>
                  <Badge className={`text-[10px] border ${gBadge.cls}`}>{gBadge.text}</Badge>
                </div>
                <ul className="divide-y divide-slate-100 text-[12px]">
                  {rows.map((r) => (
                    <li key={r.key} className="flex items-center justify-between px-3 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {r.set
                          ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                          : (r.criticality === 'required'
                              ? <XCircle size={13} className="text-rose-500 shrink-0" />
                              : r.criticality === 'recommended'
                                ? <AlertCircle size={13} className="text-amber-500 shrink-0" />
                                : <Circle size={13} className="text-slate-300 shrink-0" />)}
                        <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded truncate">{r.key}</code>
                      </div>
                      <div className="flex items-center gap-2 text-[10.5px] text-slate-500 shrink-0">
                        <span className="capitalize">{r.criticality}</span>
                        {r.aliases?.length > 0 && !r.set && (
                          <span title={`Aliases also checked: ${r.aliases.join(', ')}`}>
                            · {r.aliases.length} alias{r.aliases.length > 1 ? 'es' : ''}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {(status.missing || []).length > 0 && (
            <div className="mt-2 border-t border-slate-200 pt-3">
              <p className="text-[11px] text-slate-500 mb-2">
                Legacy &quot;critical missing&quot; checklist (blocks outbound sending):
              </p>
              <ul className="text-sm space-y-1" data-testid="twilio-missing-list">
                {(status.missing || []).map((m) => (
                  <li key={m} className="flex items-center gap-2">
                    <XCircle size={12} className="text-slate-400" />
                    <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{m}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[11px] text-slate-500">
            Set env vars in the backend and restart the service — the Twilio
            provider re-reads config on the next request.
          </p>
        </CardContent>
      </Card>

      {/* Opt-out keyword catalogue */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert size={16} className="text-slate-600" />
            Advanced Opt-Out keywords
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <KeywordBlock title="Opt-Out" tone="rose"
            en={status.opt_out_keywords?.en} es={status.opt_out_keywords?.es} />
          <KeywordBlock title="Opt-In" tone="emerald"
            en={status.opt_in_keywords?.en} es={status.opt_in_keywords?.es} />
          <KeywordBlock title="Help" tone="sky"
            en={status.help_keywords?.en} es={status.help_keywords?.es} />
        </CardContent>
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
function ConfigRow({ label, configured, preview, envKey, testId }) {
  return (
    <div className="p-2.5 rounded-md border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {STATUS_PILL(configured, configured ? 'Configured' : 'Missing')}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        env: <code className="bg-slate-100 px-1 py-0.5 rounded">{envKey}</code>
        {preview && <> · <span className="font-mono text-slate-700">{preview}</span></>}
      </div>
    </div>
  );
}

function PillarRow({ label, ok, statusLabel, soft }) {
  return (
    <div className="flex items-center justify-between p-2 rounded border border-slate-200 bg-white">
      <div>
        <div className="text-slate-800">{label}</div>
        {soft && <div className="text-[10px] text-slate-400">{soft}</div>}
      </div>
      {statusLabel
        ? <Badge variant="outline" className={`text-[10px] ${ok ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-amber-50 text-amber-800 border-amber-300'}`}>{statusLabel}</Badge>
        : STATUS_PILL(ok, ok ? 'Ready' : 'Not ready')}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-dashed border-slate-100 pb-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium">{value}</span>
    </div>
  );
}

const TWILIO_ERROR_HINTS = {
  '30003': 'The destination handset is unreachable (powered off or out of service).',
  '30004': 'Message blocked by the destination carrier (opt-out or spam filter).',
  '30005': 'Destination number is unknown or unassigned.',
  '30006': 'Landline / unreachable carrier — SMS cannot be delivered to this number.',
  '30007': 'Carrier filtering — the carrier rejected the message as spam.',
  '30008': 'Unknown delivery failure.',
  '30034': 'The sender number is not registered to an approved A2P 10DLC campaign. Register +18137012300 (agent channel) inside a Campaign in Twilio Console → Messaging → Regulatory → US A2P 10DLC, then re-test.',
  '30035': 'Toll-Free verification not approved for this number yet.',
  '30036': 'Message expired (older than the validity window).',
  '30038': 'A2P 10DLC number not associated with a campaign in the Messaging Service.',
  '21610': 'The destination number has replied STOP and is opted-out.',
  '21611': 'This "From" number has exceeded the queue size.',
  '21614': 'Destination number is not a valid mobile number.',
};

function humanizeTwilioError(code) {
  if (!code) return null;
  return TWILIO_ERROR_HINTS[String(code)] || null;
}

function ChannelCard({ channel, title, ready, number, mssidPreview, envNumber, envMssid, notReadyHint }) {
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);

  const doSend = async () => {
    const to = testTo.trim();
    if (!to) {
      toast.error('Enter a phone number in E.164 (+1…) or 10-digit US format');
      return;
    }
    setSending(true);
    setLastResult(null);
    setLiveStatus(null);
    try {
      const { data } = await smsIntegrationAPI.testSms({ to, channel });
      setLastResult(data);
      if ((data.status || '').toLowerCase() === 'failed') {
        toast.error(`Twilio marked the message failed: ${data.error_code || 'unknown'}`);
      } else {
        toast.success(`Test SMS accepted by Twilio (status=${data.status}). Check the destination phone.`);
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Test SMS failed'));
    } finally {
      setSending(false);
    }
  };

  const doCheckStatus = async () => {
    if (!lastResult?.provider_message_id) return;
    setCheckingStatus(true);
    try {
      const { data } = await smsIntegrationAPI.messageStatus(lastResult.provider_message_id);
      setLiveStatus(data);
      const st = (data.status || '').toLowerCase();
      if (st === 'delivered') {
        toast.success(`Twilio confirms DELIVERED to ${data.to}`);
      } else if (st === 'failed' || st === 'undelivered') {
        toast.error(`Not delivered: ${data.error_code || '?'} — ${data.error_message || 'no reason returned'}`);
      } else {
        toast.info(`Twilio status: ${st || 'unknown'}`);
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not fetch Twilio status'));
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <div
      className={`p-3 rounded-md border ${ready ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}
      data-testid={`channel-card-${channel}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-sm text-slate-900">{title}</div>
        {ready
          ? <Badge className="bg-emerald-100 text-emerald-800">Ready</Badge>
          : <Badge className="bg-amber-100 text-amber-800">Not configured</Badge>}
      </div>
      <div className="text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-slate-500">Number</span>
          <span className="font-mono text-slate-800">{number || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">MSSID</span>
          <span className="font-mono text-slate-800 text-[10px]">{mssidPreview || '—'}</span>
        </div>
        <div className="text-slate-400 text-[10px] pt-1">
          Env: <code>{envNumber}</code> · <code>{envMssid}</code>
        </div>
        {notReadyHint}
      </div>

      {ready && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          {!testOpen ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTestOpen(true)}
              className="w-full text-[11px]"
              data-testid={`channel-card-${channel}-test-toggle`}
            >
              <Send size={12} className="mr-1" /> Send test SMS
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                Test recipient (usually your own cell)
              </div>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="+18135551234"
                  className="flex-1 h-8 rounded border border-slate-300 px-2 text-xs font-mono"
                  data-testid={`channel-card-${channel}-test-to`}
                />
                <Button
                  size="sm"
                  onClick={doSend}
                  disabled={sending || !testTo.trim()}
                  className="text-[11px]"
                  data-testid={`channel-card-${channel}-test-send`}
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : 'Send'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setTestOpen(false); setLastResult(null); }}
                  className="text-[11px]"
                  data-testid={`channel-card-${channel}-test-cancel`}
                >
                  Cancel
                </Button>
              </div>
              {lastResult && (
                <div
                  className={`rounded border px-2 py-1.5 text-[11px] ${
                    (lastResult.status || '').toLowerCase() === 'failed'
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  }`}
                  data-testid={`channel-card-${channel}-test-result`}
                >
                  <div>Status: <strong>{lastResult.status}</strong></div>
                  {lastResult.provider_message_id && <div>Message ID: <code className="text-[10px]">{lastResult.provider_message_id}</code></div>}
                  {lastResult.resolved_phone_number && (
                    <div>Sent from: <code className="text-[10px]">{lastResult.resolved_phone_number}</code></div>
                  )}
                  {lastResult.resolved_messaging_service_sid_preview && (
                    <div>MSSID used: <code className="text-[10px]">{lastResult.resolved_messaging_service_sid_preview}</code></div>
                  )}
                  {lastResult.error_code && <div>Error: {lastResult.error_code} — {lastResult.error_message}</div>}
                  {lastResult.provider_message_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={doCheckStatus}
                      disabled={checkingStatus}
                      className="mt-1.5 text-[10.5px] h-6"
                      data-testid={`channel-card-${channel}-check-status`}
                    >
                      {checkingStatus ? <Loader2 size={10} className="animate-spin mr-1" /> : null}
                      Check real delivery status
                    </Button>
                  )}
                </div>
              )}
              {liveStatus && (
                <div
                  className={`rounded border px-2 py-1.5 text-[11px] ${
                    (liveStatus.status || '').toLowerCase() === 'delivered'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : (liveStatus.status || '').toLowerCase() === 'failed' || (liveStatus.status || '').toLowerCase() === 'undelivered'
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}
                  data-testid={`channel-card-${channel}-live-status`}
                >
                  <div className="font-semibold">Twilio live status</div>
                  <div>Status: <strong>{liveStatus.status}</strong></div>
                  {liveStatus.from && <div>From: <code className="text-[10px]">{liveStatus.from}</code></div>}
                  {liveStatus.to && <div>To: <code className="text-[10px]">{liveStatus.to}</code></div>}
                  {liveStatus.messaging_service_sid && (
                    <div>MSSID: <code className="text-[10px]">{liveStatus.messaging_service_sid}</code></div>
                  )}
                  {liveStatus.error_code && (
                    <div>Error: {liveStatus.error_code} — {liveStatus.error_message || 'no reason returned'}</div>
                  )}
                  {humanizeTwilioError(liveStatus.error_code) && (
                    <div className="mt-1.5 rounded bg-white/70 border border-current/30 px-2 py-1.5 text-[10.5px] leading-relaxed">
                      <strong>What to fix:</strong> {humanizeTwilioError(liveStatus.error_code)}
                    </div>
                  )}
                  {liveStatus.date_sent && <div>Sent at: {liveStatus.date_sent}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function WebhookRow({ label, url, hint, testId, onCopy }) {
  return (
    <div className="p-2.5 rounded border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <Button size="sm" variant="outline" onClick={() => onCopy(url)} disabled={!url}>
          <Copy size={12} className="mr-1" /> Copy
        </Button>
      </div>
      <Input readOnly value={url || 'WEBHOOK_BASE_URL is not set'} className="font-mono text-xs" />
      <p className="text-[10px] text-slate-500 mt-1">{hint}</p>
    </div>
  );
}

function KeywordBlock({ title, tone, en, es }) {
  const toneMap = {
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
  };
  return (
    <div className={`p-2 rounded border ${toneMap[tone]}`}>
      <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide">{title}</div>
      <div className="mb-1">
        <div className="text-[10px] text-slate-500">English</div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {(en || []).map((k) => <code key={k} className="text-[10px] bg-white px-1.5 py-0.5 rounded border border-slate-200">{k}</code>)}
        </div>
      </div>
      <div>
        <div className="text-[10px] text-slate-500">Español</div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {(es || []).map((k) => <code key={k} className="text-[10px] bg-white px-1.5 py-0.5 rounded border border-slate-200">{k}</code>)}
        </div>
      </div>
    </div>
  );
}

const boolLabel = (v) => (v ? 'Enabled' : 'Disabled');

// Compliance status pill — colours match the semantic meaning:
// approved → emerald, pending → amber, rejected → rose,
// not_submitted / not_configured → slate.
function StatusPill({ label, value }) {
  const v = (value || 'not_configured').toLowerCase();
  const tone = {
    approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    pending_review: 'bg-amber-100 text-amber-800 border-amber-300',
    rejected: 'bg-rose-100 text-rose-800 border-rose-300',
    not_submitted: 'bg-slate-100 text-slate-700 border-slate-300',
    not_configured: 'bg-slate-100 text-slate-700 border-slate-300',
  }[v] || 'bg-slate-100 text-slate-700 border-slate-300';
  const humanValue = v.replace(/_/g, ' ');
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <Badge variant="outline" className={`text-[11px] font-normal capitalize ${tone}`} data-testid={`status-pill-${label.toLowerCase().replace(/\s+/g,'-').replace('/','-')}`}>
        {humanValue}
      </Badge>
    </div>
  );
}



function PhoneDiagnosticsCard() {
  const [phone, setPhone] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!phone.trim()) { toast.error('Enter a phone number first'); return; }
    setLoading(true);
    try {
      const r = await smsIntegrationAPI.phoneDiagnostics(phone.trim());
      setData(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Diagnostic query failed'));
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="twilio-phone-diagnostics">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Search size={16} className="text-sky-600" />
          Per-Phone Diagnostics
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Paste a phone number (E.164 or 10 digits) and see EXACTLY where
          it lives in the SMS pipeline: matcher output on each channel,
          every conversation it belongs to, and the last 20 messages.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          <Input
            value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="+14076349936"
            data-testid="phone-diag-input"
          />
          <Button onClick={run} disabled={loading} data-testid="phone-diag-run">
            {loading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Search size={12} className="mr-1" />}
            Trace
          </Button>
        </div>
        {data && (
          <div className="space-y-4 text-xs">
            <div>
              <div className="font-medium text-slate-700 mb-1">Input</div>
              <div className="p-2 bg-slate-50 rounded font-mono">
                {data.input.phone} → normalized: {data.input.normalized}
              </div>
            </div>
            <div>
              <div className="font-medium text-slate-700 mb-1">Matcher output</div>
              <div className="grid grid-cols-2 gap-2">
                {['on_customer_channel', 'on_agent_channel'].map((k) => (
                  <div key={k} className="p-2 bg-slate-50 rounded">
                    <div className="text-[10px] uppercase text-slate-500 mb-1">{k.replace('on_', '').replace('_', ' ')}</div>
                    <div className="font-mono">state: <strong>{data.matcher[k].state}</strong></div>
                    <div className="font-mono">candidates: {data.matcher[k].candidates.length}</div>
                    {data.matcher[k].candidates.map((c, i) => (
                      <div key={i} className="text-[10px] mt-1 pl-2 border-l-2 border-slate-300">
                        {c.contact_type}: {c.display_name} ({(c.contact_id || '').slice(0, 8)})
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-medium text-slate-700 mb-1">Conversations ({data.conversations.length})</div>
              <div className="max-h-40 overflow-y-auto border rounded">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-1 px-2 font-medium">Type</th>
                      <th className="text-left py-1 px-2 font-medium">Contact ID</th>
                      <th className="text-left py-1 px-2 font-medium">Convo ID</th>
                      <th className="text-left py-1 px-2 font-medium">Channel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.conversations.map((c) => (
                      <tr key={c.conversation_id} className="border-t">
                        <td className="py-1 px-2"><Badge className="bg-slate-100 text-slate-700">{c.contact_type}</Badge></td>
                        <td className="py-1 px-2 font-mono">{(c.contact_id || '—').slice(0, 12)}</td>
                        <td className="py-1 px-2 font-mono">{(c.conversation_id || '').slice(0, 12)}</td>
                        <td className="py-1 px-2 text-slate-500">{c.channel_number || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="font-medium text-slate-700 mb-1">Recent messages ({data.messages.length})</div>
              <div className="max-h-64 overflow-y-auto border rounded">
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-1 px-2 font-medium">When</th>
                      <th className="text-left py-1 px-2 font-medium">Dir</th>
                      <th className="text-left py-1 px-2 font-medium">Type</th>
                      <th className="text-left py-1 px-2 font-medium">Body</th>
                      <th className="text-left py-1 px-2 font-medium">Convo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.messages.map((m) => (
                      <tr key={m.message_id} className="border-t">
                        <td className="py-1 px-2 text-slate-500 whitespace-nowrap">
                          {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}
                        </td>
                        <td className="py-1 px-2">
                          <Badge className={m.direction === 'inbound' ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-700'}>
                            {m.direction}
                          </Badge>
                        </td>
                        <td className="py-1 px-2 font-mono">{m.contact_type || '—'}</td>
                        <td className="py-1 px-2 text-slate-700 max-w-[200px] truncate">{m.body || '—'}</td>
                        <td className="py-1 px-2 font-mono text-[10px]">{(m.conversation_id || '').slice(0, 8)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function InboundDiagnosticsCard() {
  const [data, setData] = useState({ items: [], counts: { signature_ok: 0, signature_failed: 0 } });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await smsIntegrationAPI.inboundDiagnostics({ limit: 20 });
      setData(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load inbound diagnostics'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const okCount = data.counts?.signature_ok || 0;
  const failCount = data.counts?.signature_failed || 0;
  const hasFailures = failCount > 0;

  return (
    <Card data-testid="twilio-inbound-diagnostics">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Webhook size={16} className="text-sky-600" />
            Inbound Webhook Diagnostics
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="diag-refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Every attempt Twilio makes to POST to `/api/integrations/twilio/inbound` is logged here.
          If <strong>Signature failed &gt; 0</strong>, the webhook URL configured in the Twilio
          Messaging Service Console does not match the URL the backend is computing. Common
          cause: <code>WEBHOOK_BASE_URL</code> env var is stale.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
            <div className="text-2xl font-semibold text-emerald-800" data-testid="diag-ok-count">{okCount}</div>
            <div className="text-xs text-emerald-700">Signature validated</div>
          </div>
          <div className={`p-3 rounded-md border ${hasFailures ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className={`text-2xl font-semibold ${hasFailures ? 'text-rose-800' : 'text-slate-500'}`} data-testid="diag-fail-count">{failCount}</div>
            <div className={`text-xs ${hasFailures ? 'text-rose-700' : 'text-slate-500'}`}>Signature failed</div>
          </div>
        </div>

        {data.items?.length ? (
          <div className="max-h-64 overflow-y-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">When</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Kind</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">From</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">To</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Body</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Fwd Host</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, idx) => {
                  const isSuccess = it.kind === 'signature_ok' || (it.kind || '').startsWith('trace_');
                  const isFail = it.kind === 'signature_failed' || (it.kind || '').endsWith('_error') || it.kind === 'bailed_missing_from_or_sid';
                  return (
                    <tr key={idx} className="border-t">
                      <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">
                        {it.at ? new Date(it.at).toLocaleString() : '—'}
                      </td>
                      <td className="py-1.5 px-2">
                        <Badge className={isFail
                          ? 'bg-rose-100 text-rose-800'
                          : isSuccess
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-100 text-slate-700'}>
                          {it.kind}
                        </Badge>
                      </td>
                      <td className="py-1.5 px-2 text-slate-700">{it.from || '—'}</td>
                      <td className="py-1.5 px-2 text-slate-700">{it.to || '—'}</td>
                      <td className="py-1.5 px-2 text-slate-500 max-w-[180px] truncate">
                        {it.body_preview || it.contact_type || it.state || it.channel || it.conversation_id?.slice(0, 8) || '—'}
                      </td>
                      <td className="py-1.5 px-2 text-slate-500 text-[10px]">{it.forwarded_host || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-4 text-center">
            No inbound webhook attempts yet.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
